





































































import SubnavigationArrowButton from './SubnavigationArrowButton.vue';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { SubnavigationArrowsData, SubnavigationHiddenObject } from './Subnavigation';
import {
  CONSIDERED_VISIBLE_THRESHOLD,
  SUBNAVIGATION_ARROW_BACKGROUND_WIDTH,
  SUBNAVIGATION_DEBOUNCE_TIME,
} from './Subnavigation.config';
import { createEventListener } from '@owc-utils/Mixins/EventListenerMixin';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';

export default ComponentInitializeDispatcher.extend({
  name: 'owc-subnavigation-arrows',
  components: {
    SubnavigationArrowButton,
    SlotContainer,
  },
  props: {
    navigationItems: {
      required: true,
      type: Array,
    },
    isMobile: {
      required: true,
      type: Boolean,
    },
    displayLabel: {
      required: true,
      type: String,
    },
    shouldShowToggleB2BB2C: {
      required: true,
      type: Boolean,
    },
    isTopbarVisible: {
      required: true,
      type: Boolean,
    },
  },
  mixins: [
    createEventListener('resize', 'resizeEventHandler', {
      debounceTime: SUBNAVIGATION_DEBOUNCE_TIME,
    }),
  ],
  data(): SubnavigationArrowsData {
    return {
      isLeftArrowVisible: false,
      isRightArrowVisible: true,
      subnavigationOverflowAmount: 0,
      scrollThreshold: CONSIDERED_VISIBLE_THRESHOLD,
      hasSubnavigationListScrollHandler: false,
      scrollArrowBackgroundWidth: SUBNAVIGATION_ARROW_BACKGROUND_WIDTH,
      subnavigationItemsSizeMap: {} as SubnavigationHiddenObject,
      resizeHandler: null,
    };
  },
  mounted() {
    this.resizeEventHandler();
  },
  methods: {
    /*
     * This function checks if the arrows should be shown or not. Both of them, regardless if
     * individually they should be shown or not. Visibility of the arrows based on scroll distance
     * (hiding the right arrow when the user hasn't scrolled for example)
     * is done on handleHorizontalNavigation method
     *
     * This function updates the value directly
     */
    updateArrowVisibility: function(): void {
      const scrollWidth = (this.$refs.subnavigationItemsList as HTMLElement).scrollWidth;
      const visibleSubnavigationSize = (this.$refs.subnavigationItemsList as HTMLElement).clientWidth;

      this.subnavigationOverflowAmount = scrollWidth - visibleSubnavigationSize;

      const shouldShowArrows = !this.isMobile && this.subnavigationOverflowAmount > 0;

      this.isRightArrowVisible = shouldShowArrows;
      this.isLeftArrowVisible = shouldShowArrows;
    },
    /*
     *
     * Iterativly sums all available items so that the scroll
     * amount is calculated only once and on page load, instead of
     * on click.
     *
     * The calculation is done like this:
     * [nextItem, nextItem2. nextItem3].reverse()
     *
     * scrollToNextItem3 = nextIte3 + nextItem2 + nextItem1
     * scrollToNextItem2 = nextItem2 + nextItem1
     * scrollToNextItem1 = nextItem1
     *
     * extraOffset ensures that when we scroll to the left
     * we take into account that the list as already scrolled to the right before
     * meaning that scrolling one item to the left is not equal to nextItemSize but
     * in fact  alreadyScrolledValue - nextItemSize;
     *
     * The returned array will have the amounts needed to scroll to that element,
     * meaning that if the returned array for the right side of the navigation
     * is [-324] this means that the subnavigation only has 1 item hidden on the right side
     * and on clicking the arrow the subnavigation will scroll 324px to the right;
     * (negative numberes ensure that we scroll to the right)
     */
    generateOverflowArray(values: number[], extraOffset = 0) {
      const totalSum = values.reduce((total, value) => total + value, 0);
      const overflowArray = Array(values.length);

      let currentSum = totalSum;

      for (let i = 0; i < values.length; i++) {
        overflowArray[i] = -Math.abs(currentSum + extraOffset);
        currentSum -= values[i];
      }
      return overflowArray;
    },
    /*
     * Define which elements will be hidden when the subnavigation is scrolled
     * There's also a check before the break with the overflowThreshold variable
     * which ensures that items that are partially hidden are scrolled into view.
     * The threshold (this.scrollThreshold which is a value between 0 and 1)
     * is used to define how much needs to be visible until the item is recognized as "visible".
     * We do this because an item that as 5px of its width hidden by the arrow,
     * is still fully visible to the user and there's no need to scroll just those
     * 5px.
     * To ensure that the calculation still work in these cases we fake the size of the semi-visible
     * element. (|| => screen edges)
     *
     * || [element] [element] [eleme || nt] [hiddenElement]
     *                        ------   -----
     *                        200px  +  90px
     *
     * In this case the element size pushed into the arrya will be only 90px, since that's what is need
     * for the element to be fully visible
     *
     * @items - List of subnavigation items
     * @overflowAmount - Amount in px that are hidden when the bar is fully scrolled to one side
     *
     * @returns elementSizes - Which is an array with the sizes of all the hidden elements
     */
    generateHiddenElementSizes(items: Element[], overflowAmount: number) {
      const elementSizes = [];

      let currentSize = 0;
      for (let i = 0; i < items.length; i++) {
        const elementSize = items[i].clientWidth;
        // Since the threshold is a percentage it's dependant on the elements size
        const elementThreshold = elementSize * this.scrollThreshold;
        const overflowPlusVisibleElementWidth = overflowAmount + elementThreshold;

        currentSize += elementSize;

        if (currentSize <= overflowAmount) {
          // Store the negative width to facilitate further calculations
          elementSizes.push(-elementSize);
        } else {
          if (currentSize <= overflowPlusVisibleElementWidth) {
            const elementHiddenWidth = elementSize - (currentSize - overflowAmount);
            //Push only the hidden part of the element
            elementSizes.push(-elementHiddenWidth);
          }

          // has exceed threshold, the rest is hidden, we can stop calculating sizes
          break;
        }
      }

      return elementSizes;
    },
    /*
     * Calculate the distance needed to scroll for
     * all the hidden navigation items (both left and right)
     * so that on arrow click we scroll only one item in the
     * desired direction.
     *
     * To check which items are hidden [assume that '|' represents the limits of the screen]:
     *
     * | [item] [item] [item] | [hiddenItem] [hiddenItem]
     * -----------------------
     *     Visible Scroll
     *
     * | [item] [item] [item] | [hiddenItem] [hiddenItem]
     * --------------------------------------------------
     *                   ScrollWidth
     *
     * So the [hidden section] is equal to scrollWidth - visible scroll
     * Then each item.width is added to each other in orther until
     * sum is > [hidden section]. And this will give us the amount of items
     * that are inside this hidden section
     */
    setSubnavigationItemsSizeMap: function(): void {
      const subnavigationItemListElement = this.$refs.subnavigationItemsList as HTMLElement;
      const items = Array.from(subnavigationItemListElement.children);
      const overflowAmount = this.subnavigationOverflowAmount + this.scrollArrowBackgroundWidth;

      let leftArray = this.generateHiddenElementSizes(items, overflowAmount);
      leftArray = this.generateOverflowArray(leftArray, this.subnavigationOverflowAmount);
      leftArray[0] = 0;

      let rightArray = this.generateHiddenElementSizes(items.reverse(), overflowAmount);
      rightArray = this.generateOverflowArray(rightArray);
      rightArray[0] = -this.subnavigationOverflowAmount;

      this.subnavigationItemsSizeMap.left = {
        currentIndex: 0,
        direction: 'left',
        values: leftArray,
      };
      this.subnavigationItemsSizeMap.right = {
        currentIndex: rightArray.length - 1,
        direction: 'right',
        values: rightArray,
      };

      this.isLeftArrowVisible = false;
      this.changeHorizontalOffset('left');
    },
    resizeEventHandler(): void {
      const subnavigationListElement = this.$refs.subnavigationListContainer as HTMLElement;

      /*
       * Until accessibility is tackled this is an easy solution to have a working
       * scrollbar. It's not ideal, since we are removing a feature, but leaving both
       * would just create confusion since our calculations do not account for the fact
       * that the user might scroll the navigation. On the other hand if they are scrolling
       * there's no need to have arrows.
       *
       * If the user resizes the window, the arrows will be reenabled (but the scroll will remain active)
       * Again not the best solution but i think this is better than leaving everything to
       * this accessibility ticket that has no sprint date yet.
       */
      if (subnavigationListElement && !this.hasSubnavigationListScrollHandler) {
        this.hasSubnavigationListScrollHandler = true;
        subnavigationListElement.addEventListener(
          'scroll',
          () => {
            this.isLeftArrowVisible = false;
            this.isRightArrowVisible = false;
            this.hasSubnavigationListScrollHandler = false;
          },
          { once: true, passive: true },
        );
      }

      this.$nextTick(() => {
        if (this.$refs.subnavigationItemsList) {
          this.updateArrowVisibility();

          if (!this.isMobile) {
            this.setSubnavigationItemsSizeMap();
          }
        }
      });
    },
    /*
     * This function checks if the pointer on the opposite side of the button click
     * should updated or not. If the item on the opposite side is bigger than the
     * next 2 items on the clicked side, we don't want to move the pointer.
     *
     * This might be confusing so check the exmaple bellow(|| => represent the edges of the screen):
     *
     * || [super mega giant big visible item] [visibleItem] [visibleItem] >|| [item] [item] [item]
     *
     * In this example the list has not yet scrolled, if we click on the right arrow this will be the next state:
     *
     * [super mega ||< giant big visible item] [visibleItem] [visibleItem] [visibleItem] > || [item] [item]
     *
     * As you can see the pointer on the left side should still point to 0, and that's what the @shouldUpdatePointer
     * represents.
     */
    updateOppositeArrayPointerValue: function(
      scrollValue: number,
      sizeDifference: number,
      isOppositeDirectionRight: boolean,
    ): void {
      const direction = isOppositeDirectionRight ? 'right' : 'left';

      const currentArrayIdx = this.subnavigationItemsSizeMap[direction].currentIndex;
      const arraySize = this.subnavigationItemsSizeMap[direction].values.length;
      const nextArrayIdx = Math.min(currentArrayIdx + 1 + sizeDifference, arraySize - 1);
      const shouldUpdatePointer = isOppositeDirectionRight
        ? scrollValue > this.subnavigationItemsSizeMap[direction].values[nextArrayIdx]
        : scrollValue < this.subnavigationItemsSizeMap[direction].values[nextArrayIdx];

      if (shouldUpdatePointer) {
        this.subnavigationItemsSizeMap[direction].currentIndex = nextArrayIdx;
      } else if (currentArrayIdx === -1) {
        this.subnavigationItemsSizeMap[direction].currentIndex = 0;
      }
    },
    changeHorizontalOffset: function(direction: string) {
      const scrollAmountObject = this.subnavigationItemsSizeMap[direction];
      let scrollValue = -1;

      if (scrollAmountObject) {
        const scrollAmountArray = scrollAmountObject.values;
        const currentIndex = scrollAmountObject.currentIndex;
        const subnavigationItemsListElement = this.$refs.subnavigationItemsList as HTMLElement;

        scrollValue = scrollAmountArray[currentIndex];

        subnavigationItemsListElement.style.setProperty('--subnavigation-scroll-value', String(scrollValue));

        this.subnavigationItemsSizeMap[direction].currentIndex = Math.max(-1, currentIndex - 1);
      }

      return scrollValue;
    },
    /*
     * Handles the scrolling of the subnavigation ensuring that the next item
     * shown on each side is always correct.
     * So (assuming that '||' is the edge of the screen):
     *
     * || [leftItem] [leftItem] [leftItem] || [rightItems] [rightItems] [rightItems]
     *                                             ^
     *                                        rightPointer
     *
     * If we scroll one item to the right we need to update the right pointer
     * and add the left pointer in case we want to scroll back:
     *
     * [leftItem] || [leftItem] [leftItem] [rightItems] || [rightItems] [rightItems]
     *     ^                                                     ^
     * leftPointer                                          rightPointer
     *
     * And if we click right again:
     *
     * [leftItem] [leftItem] || [leftItem] [rightItems] [rightItems] || [rightItems]
     *                ^                                                       ^
     *            leftPointer                                           rightPointer
     */
    handleHorizontalNavigation: function(isScrollingRight: boolean): void {
      /*
       * Used in situations where the arrays have different sizes,
       * and this needs to be taken into account when setting the pointers
       */
      const leftArraySize = this.subnavigationItemsSizeMap.left.values.length;
      const rightArraySize = this.subnavigationItemsSizeMap.right.values.length;
      const sizeDifference = Math.abs(leftArraySize - rightArraySize);

      let scrollValue = -1;

      if (isScrollingRight) {
        this.isLeftArrowVisible = true;

        scrollValue = this.changeHorizontalOffset('right');

        this.updateOppositeArrayPointerValue(scrollValue, sizeDifference, false);

        if (this.subnavigationOverflowAmount + scrollValue === 0) {
          //Ensures that the scroll animation is always visible even on situations with "layout trashing"
          this.$nextTick(() => (this.isRightArrowVisible = false));
        }
      } else {
        this.isRightArrowVisible = true;

        scrollValue = this.changeHorizontalOffset('left');

        this.updateOppositeArrayPointerValue(scrollValue, sizeDifference, true);

        if (scrollValue === 0) {
          //Ensures that the scroll animation is always visible even on situations with "layout trashing"
          this.$nextTick(() => (this.isLeftArrowVisible = false));
        }
      }
    },
  },
});
